import { every, filter, isEmpty, some } from 'lodash';
import { useSelector } from 'react-redux';

import { StatusItem } from '@/apps/health_check/types/IHealthCheck';
import {
  StatusItemIssueSeverity,
  StatusItemIssueType,
  StatusItemResponsibility,
} from '@/apps/health_check/types/enums';
import { RootState } from '@/redux/store';

// ---- EXTERNAL SELECTORS ----

export const selectStatusItemsFilters = (state: RootState) =>
  state.statusItemsFilters;

export const selectStatusItemsSorting = (state: RootState) =>
  state.statusItemsSorting.sortOptions;

// ---- RTK QUERY FILTERS ----

export const useStatusItemFilters = (
  responsibilityId?: StatusItemResponsibility
): { [key: string]: FilterFn | undefined } => {
  const filters = useSelector(selectStatusItemsFilters);

  return {
    // Narrowing to a single phase is done server-side through the
    // Responsibility query parameter; this predicate only backs it up. Without
    // a responsibility every phase is kept, so callers that omit it get the
    // whole fleet instead of an empty list.
    responsibilityId: responsibilityId
      ? (item: StatusItem) => item.responsibilityId === responsibilityId
      : undefined,
    muted: (item: StatusItem) => filters.showMuted || !item.isMuted,
    healthy: (item: StatusItem) =>
      filters.showHealthy || isEmpty(item.statusItemIssues),
    contract: (item: StatusItem) => {
      if (!filters.flattenContracts || isEmpty(filters.flattenContracts)) {
        return true;
      }

      const contractMatch = filters.flattenContracts.some((contractInfo) => {
        const contractId = contractInfo[2];
        return item.contractId?.toString() === contractId;
      });
      return contractMatch;
    },
    severity: (item: StatusItem) => {
      if (isEmpty(filters.severity)) {
        return true;
      }

      const itemSeverities = new Set(
        item.statusItemIssues?.map((issue) => issue.severity)
      );
      return filters.severity.some((severity) => itemSeverities.has(severity));
    },
    type: (item: StatusItem) => {
      const hasTypes = !isEmpty(filters.types);
      const hasFaultAlarms = !isEmpty(filters.flattenFaultAlarms);

      if (!hasTypes && !hasFaultAlarms) {
        return true;
      }

      let matchesType = false;
      let matchesFaultAlarm = false;

      if (hasTypes) {
        const relevantIssues =
          filters.severity.length > 0
            ? item.statusItemIssues?.filter(
                (error) =>
                  error.severity !== undefined &&
                  filters.severity.includes(error.severity)
              )
            : item.statusItemIssues;

        const vehicleErrorTypes = new Set(
          relevantIssues?.map((error) => error.type)
        );

        matchesType = filters.types.some((type) => vehicleErrorTypes.has(type));
      }

      if (hasFaultAlarms) {
        const faultAlarmIssues = item.statusItemIssues?.filter(
          (issue) => issue.type === StatusItemIssueType.FaultAlarm
        );

        if (faultAlarmIssues && faultAlarmIssues.length > 0) {
          const itemFaultAlarmNames = new Set(
            faultAlarmIssues.map((issue) => issue.displayName?.trim())
          );

          matchesFaultAlarm = filters.flattenFaultAlarms.some(
            (faultAlarmPath) => {
              const selectedContractId = faultAlarmPath[0];
              const selectedResponsibilityId = faultAlarmPath[1];
              const selectedDisplayName = faultAlarmPath[3]?.trim();

              return (
                item.contractId?.toString() === selectedContractId &&
                item.responsibilityId?.toString() ===
                  selectedResponsibilityId &&
                selectedDisplayName &&
                itemFaultAlarmNames.has(selectedDisplayName)
              );
            }
          );
        }
      }

      if (hasTypes && hasFaultAlarms) {
        return matchesType || matchesFaultAlarm;
      } else if (hasTypes) {
        return matchesType;
      } else {
        return matchesFaultAlarm;
      }
    },
    vehicleId: (item: StatusItem) => {
      if (isEmpty(filters.vehicleIds)) {
        return true;
      }

      return item.vehicleId?.toString().includes(filters.vehicleIds) || false;
    },

    blockId: (item: StatusItem) => {
      if (isEmpty(filters.blockIds)) {
        return true;
      }

      return (
        item.blockExternalId?.toLowerCase().includes(filters.blockIds) || false
      );
    },
  };
};

type FilterFn = (item: StatusItem) => boolean;

export function applyFiltersToStatusItems(
  items: StatusItem[],
  filters: FilterFn[]
) {
  return filter(items, (item) =>
    every(filters, (singleFilter) => singleFilter(item))
  );
}

export const getCriticalIssuesCount = (statusItems: StatusItem[]) =>
  filter(statusItems, (statusItem) =>
    some(statusItem.statusItemIssues, {
      severity: StatusItemIssueSeverity.High,
    })
  ).length;
